/**
 * Session Service
 * Handles all session-related API calls for the frontend
 */

import { getServerUrl } from '../config/runtimeConfig';

const API_BASE_URL = getServerUrl();

class SessionService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/sessions`;
  }

  /**
   * Get authentication headers for API requests
   * @returns {Object} Headers object with authorization if token exists
   */
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Handle API response and check for authentication errors
   * @param {Response} response - Fetch response object
   * @returns {Promise} Response JSON or throws error
   */
  async handleResponse(response) {
    if (response.status === 401) {
      // Handle unauthorized access
      const hasToken = localStorage.getItem('token');
      const isAuthPage = window.location.pathname === '/auth';
      
      if (hasToken) {
        // Token exists but is invalid, remove it
        localStorage.removeItem('token');
      }
      
      // Only redirect if we're not already on the auth page
      if (!isAuthPage) {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/auth?from=${encodeURIComponent(currentPath)}`;
      }
      
      throw new Error('Authentication required');
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Get all sessions for the current user
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Sessions response with pagination
   */
  async getSessions(options = {}) {
    const {
      page = 1,
      limit = 20,
      status = 'all',
      sortBy = 'updatedAt',
      sortOrder = 'desc',
      search = ''
    } = options;

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      status,
      sortBy,
      sortOrder
    });

    if (search) {
      params.append('search', search);
    }

    try {
      const response = await fetch(`${this.baseURL}?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('SessionService.getSessions error:', error);
      throw error;
    }
  }

  /**
   * Get a specific session by ID
   * @param {string} sessionId - Session unique ID
   * @returns {Promise<Object>} Session data
   */
  async getSession(sessionId) {
    try {
      const response = await fetch(`${this.baseURL}/${sessionId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('SessionService.getSession error:', error);
      throw error;
    }
  }

  /**
   * Create a new session
   * @param {Object} sessionData - Session creation data
   * @returns {Promise<Object>} Created session
   */
  async createSession(sessionData) {
    const { name, description = '', tags = [], settings = {} } = sessionData;

    if (!name || name.trim() === '') {
      throw new Error('Session name is required');
    }

    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          tags: Array.isArray(tags) ? tags.filter(tag => tag.trim()) : [],
          settings: {
            autoAnalysis: settings.autoAnalysis || false,
            analysisPrompt: settings.analysisPrompt || '',
            ...settings
          }
        })
      });

      const result = await this.handleResponse(response);
      return result.session;
    } catch (error) {
      console.error('SessionService.createSession error:', error);
      throw error;
    }
  }

  /**
   * Update an existing session
   * @param {string} sessionId - Session unique ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated session
   */
  async updateSession(sessionId, updates) {
    try {
      const response = await fetch(`${this.baseURL}/${sessionId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      const result = await this.handleResponse(response);
      return result.session;
    } catch (error) {
      console.error('SessionService.updateSession error:', error);
      throw error;
    }
  }

  /**
   * Update wizard navigation progress (currentStep, maxReachedStep) in the database.
   * Fire-and-forget — errors are silently swallowed to avoid disrupting navigation.
   * @param {string} sessionId - Session unique ID
   * @param {{ currentStep?: number, maxReachedStep?: number }} progress
   */
  async updateWizardProgress(sessionId, progress) {
    try {
      await fetch(`${this.baseURL}/${sessionId}/wizard-progress`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(progress)
      });
    } catch {
      // ignore – non-critical
    }
  }

  /**
   * Delete a session
   * @param {string} sessionId - Session unique ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteSession(sessionId) {
    try {
      const response = await fetch(`${this.baseURL}/${sessionId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('SessionService.deleteSession error:', error);
      throw error;
    }
  }

  /**
   * Get images in a specific session
   * @param {string} sessionId - Session unique ID
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Images response with pagination
   */
  async getSessionImages(sessionId, options = {}) {
    const {
      page = 1,
      limit = 20,
      sortBy = 'createdAt',
      sortOrder = 'desc',
      search,
      mediaType
    } = options;

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder
    });

    // Add search parameter if provided
    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    if (mediaType && mediaType.trim()) {
      params.append('mediaType', mediaType.trim());
    }

    try {
      const response = await fetch(`${this.baseURL}/${sessionId}/images?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('SessionService.getSessionImages error:', error);
      throw error;
    }
  }

  /**
   * Get recent sessions for quick access
   * @param {number} limit - Number of recent sessions to fetch
   * @returns {Promise<Array>} Recent sessions array
   */
  async getRecentSessions(limit = 5) {
    try {
      const response = await this.getSessions({
        page: 1,
        limit,
        status: 'active',
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      });

      return response.sessions || [];
    } catch (error) {
      console.error('SessionService.getRecentSessions error:', error);
      throw error;
    }
  }

  /**
   * Upload an image to a specific session
   * @param {string} sessionId - Session unique ID
   * @param {File} file - Image file to upload
   * @param {Object} metadata - Image metadata (name, description, tags)
   * @returns {Promise<Object>} Upload response
   */
  async uploadImageToSession(sessionId, file, metadata = {}) {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('sessionId', sessionId);
      // Add metadata if provided
      if (metadata.name) formData.append('name', metadata.name);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.tags) formData.append('tags', metadata.tags);
      // eventType handled at session level; uploads don't send it anymore

      const token = localStorage.getItem('token');
      // Use the correct backend base URL for image upload
      const uploadUrl = `${API_BASE_URL}/api/images/upload`;
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('SessionService.uploadImageToSession error:', error);
      throw error;
    }
  }

  /**
   * Get image thumbnail URL for a specific image
   * @param {string} imageId - Image unique ID
   * @returns {string} Thumbnail URL
   */
  getImageThumbnailUrl(imageId) {
    return `${API_BASE_URL}/api/images/${imageId}/thumbnail`;
  }

  /**
   * Get image serve URL for a specific image
   * @param {string} imageId - Image unique ID
   * @returns {string} Serve URL
   */
  getImageServeUrl(imageId) {
    return `${API_BASE_URL}/api/images/${imageId}/serve`;
  }

  /**
   * Validate session data before creation/update
   * @param {Object} sessionData - Session data to validate
   * @returns {Array} Array of validation errors
   */
  validateSessionData(sessionData) {
    const errors = [];

    if (!sessionData.name || sessionData.name.trim() === '') {
      errors.push('Session name is required');
    }

    if (sessionData.name && sessionData.name.length > 200) {
      errors.push('Session name must be 200 characters or less');
    }

    if (sessionData.description && sessionData.description.length > 1000) {
      errors.push('Session description must be 1000 characters or less');
    }

    return errors;
  }
}

// Create and export a singleton instance
const sessionService = new SessionService();
export default sessionService;
